/* ═══════════════════════════════════════════════════════════════════════════════
   WATRLY WATER BUTTONS 💧✨
   أزرار مائية زجاجية مذهلة — Water Button Components
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// Ripple Effect Hook — هوك تأثير التموج
// ═══════════════════════════════════════════════════════════════════════════════
const useRipple = () => {
  const [ripples, setRipples] = useState([]);
  const containerRef = useRef(null);

  const createRipple = (event) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const newRipple = {
      x,
      y,
      size,
      id: Date.now(),
    };

    setRipples((prev) => [...prev, newRipple]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
  };

  return { ripples, createRipple, containerRef };
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main Water Button — الزر المائي الرئيسي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterButton = ({ 
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  onClick,
  className = '',
  type = 'button',
  ...props
}) => {
  const { ripples, createRipple, containerRef } = useRipple();

  const handleClick = (e) => {
    if (!disabled && !loading) {
      createRipple(e);
      onClick?.(e);
    }
  };

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm rounded-lg',
    md: 'px-6 py-3 text-base rounded-xl',
    lg: 'px-8 py-4 text-lg rounded-2xl',
  };

  const variantClasses = {
    primary: `
      bg-gradient-to-br from-[#0891b2] to-[#0e7490]
      text-white
      shadow-[0_4px_18px_rgba(8,145,178,0.35),inset_0_1px_0_rgba(255,255,255,0.15)]
      hover:shadow-[0_6px_24px_rgba(8,145,178,0.45)]
      active:shadow-[0_2px_8px_rgba(8,145,178,0.25)]
      border border-[rgba(255,255,255,0.1)]
    `,
    ocean: `
      bg-gradient-to-br from-[#1d4ed8] to-[#1e40af]
      text-white
      shadow-[0_4px_18px_rgba(29,78,216,0.35),inset_0_1px_0_rgba(255,255,255,0.15)]
      hover:shadow-[0_6px_24px_rgba(29,78,216,0.45)]
      border border-[rgba(255,255,255,0.1)]
    `,
    fresh: `
      bg-gradient-to-br from-[#0d9488] to-[#0f766e]
      text-white
      shadow-[0_4px_18px_rgba(13,148,136,0.35),inset_0_1px_0_rgba(255,255,255,0.15)]
      hover:shadow-[0_6px_24px_rgba(13,148,136,0.45)]
      border border-[rgba(255,255,255,0.1)]
    `,
    glass: `
      bg-[rgba(255,255,255,0.1)]
      backdrop-blur-xl
      text-white
      border border-[rgba(255,255,255,0.2)]
      shadow-[0_4px_20px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.1)]
      hover:bg-[rgba(255,255,255,0.15)]
      hover:border-[rgba(255,255,255,0.3)]
    `,
    ghost: `
      bg-transparent
      text-[#0ABAB5]
      border-2 border-[rgba(10,186,181,0.4)]
      hover:bg-[rgba(10,186,181,0.1)]
      hover:border-[#0ABAB5]
    `,
    subtle: `
      bg-[rgba(10,186,181,0.1)]
      text-[#0ABAB5]
      hover:bg-[rgba(10,186,181,0.15)]
    `,
  };

  return (
    <button
      ref={containerRef}
      type={type}
      disabled={disabled || loading}
      onClick={handleClick}
      className={`
        relative overflow-hidden
        inline-flex items-center justify-center gap-2
        font-bold
        transition-all duration-300 ease-out
        hover:-translate-y-0.5 active:translate-y-0
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {/* Shine effect */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 50%)'
        }}
      />

      {/* Ripple effects */}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
          }}
        />
      ))}

      {/* Loading spinner */}
      {loading && (
        <svg 
          className="animate-spin w-5 h-5" 
          viewBox="0 0 24 24"
          fill="none"
        >
          <circle 
            className="opacity-25" 
            cx="12" cy="12" r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}

      {/* Icon (left) */}
      {Icon && iconPosition === 'left' && !loading && (
        <Icon className="w-5 h-5" />
      )}

      {/* Content */}
      <span className="relative z-10">{children}</span>

      {/* Icon (right) */}
      {Icon && iconPosition === 'right' && (
        <Icon className="w-5 h-5" />
      )}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Icon Button — زر الأيقونة المائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterIconButton = ({ 
  icon: Icon,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  className = '',
  ...props
}) => {
  const { ripples, createRipple, containerRef } = useRipple();

  const handleClick = (e) => {
    if (!disabled) {
      createRipple(e);
      onClick?.(e);
    }
  };

  const sizeClasses = {
    sm: 'w-9 h-9',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const variantClasses = {
    primary: `
      bg-gradient-to-br from-[#0ABAB5] to-[#089C98]
      text-white
      shadow-[0_4px_15px_rgba(10,186,181,0.4)]
      hover:shadow-[0_8px_25px_rgba(10,186,181,0.5)]
      border border-[rgba(255,255,255,0.15)]
    `,
    ocean: `
      bg-gradient-to-br from-[#0066CC] to-[#004499]
      text-white
      shadow-[0_4px_15px_rgba(0,102,204,0.4)]
      hover:shadow-[0_8px_25px_rgba(0,102,204,0.5)]
      border border-[rgba(255,255,255,0.15)]
    `,
    glass: `
      bg-[rgba(255,255,255,0.1)]
      backdrop-blur-xl
      text-white
      border border-[rgba(255,255,255,0.2)]
      shadow-[0_4px_15px_rgba(0,0,0,0.1)]
      hover:bg-[rgba(255,255,255,0.15)]
      hover:border-[rgba(255,255,255,0.3)]
    `,
    ghost: `
      bg-transparent
      text-[#0ABAB5]
      border-2 border-[rgba(10,186,181,0.4)]
      hover:bg-[rgba(10,186,181,0.1)]
      hover:border-[#0ABAB5]
    `,
  };

  return (
    <button
      ref={containerRef}
      disabled={disabled}
      onClick={handleClick}
      className={`
        relative overflow-hidden
        inline-flex items-center justify-center
        rounded-xl
        transition-all duration-300 ease-out
        hover:scale-110 active:scale-95
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${className}
      `}
      {...props}
    >
      {/* Shine effect */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30 rounded-xl"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, transparent 50%)'
        }}
      />

      {/* Ripple effects */}
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-white/30 animate-ripple pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size,
          }}
        />
      ))}

      {Icon && <Icon className={`${iconSizes[size]} relative z-10`} />}
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Floating Action Button — زر الإجراء العائم
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterFab = ({ 
  icon: Icon,
  label,
  onClick,
  position = 'bottom-right',
  className = '',
}) => {
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 left-6',
    'bottom-left': 'fixed bottom-6 right-6',
    'top-right': 'fixed top-6 left-6',
    'top-left': 'fixed top-6 right-6',
  };

  return (
    <button
      onClick={onClick}
      className={`
        ${positionClasses[position]}
        z-50
        group
        flex items-center gap-3
        pl-4 pr-2 py-2
        bg-gradient-to-br from-[#0ABAB5] to-[#0066CC]
        text-white
        rounded-full
        shadow-[0_4px_20px_rgba(10,186,181,0.5),0_8px_32px_rgba(0,0,0,0.2)]
        hover:shadow-[0_8px_30px_rgba(10,186,181,0.6),0_12px_40px_rgba(0,0,0,0.3)]
        transition-all duration-300 ease-out
        hover:scale-105 active:scale-95
        ${className}
      `}
    >
      <span className="font-bold text-sm whitespace-nowrap">{label}</span>
      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-300">
        {Icon && <Icon className="w-5 h-5" />}
      </div>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Button Group — مجموعة أزرار مائية
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterButtonGroup = ({ 
  children,
  className = '',
}) => {
  return (
    <div className={`
      inline-flex items-center
      bg-[rgba(255,255,255,0.05)]
      backdrop-blur-sm
      rounded-xl
      p-1
      border border-[rgba(255,255,255,0.1)]
      ${className}
    `}>
      {React.Children.map(children, (child, index) => (
        <div className={index !== 0 ? 'mr-1' : ''}>
          {child}
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Segmented Control — تحكم مجزأ مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterSegmentedControl = ({ 
  options = [],
  value,
  onChange,
  className = '',
}) => {
  return (
    <div className={`
      relative inline-flex items-center
      bg-[rgba(255,255,255,0.05)]
      backdrop-blur-sm
      rounded-xl
      p-1
      border border-[rgba(255,255,255,0.1)]
      ${className}
    `}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange?.(option.value)}
          className={`
            relative z-10 px-4 py-2 rounded-lg text-sm font-semibold
            transition-all duration-300
            ${value === option.value 
              ? 'text-[#0A1628]' 
              : 'text-white/60 hover:text-white'
            }
          `}
        >
          {value === option.value && (
            <div 
              className="absolute inset-0 bg-gradient-to-br from-[#0ABAB5] to-[#0066CC] rounded-lg -z-10 shadow-lg"
              layoutId="activeSegment"
            />
          )}
          {option.label}
        </button>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Action Card — بطاقة إجراء مائية
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterActionCard = ({ 
  icon: Icon,
  title,
  subtitle,
  variant = 'primary',
  onClick,
  className = '',
}) => {
  const variantStyles = {
    primary: {
      base: '#051624',
      bg: 'from-[rgba(8,145,178,0.2)] to-[rgba(14,116,144,0.1)]',
      border: 'border-[rgba(8,145,178,0.2)]',
      icon: 'from-[rgba(8,145,178,0.3)] to-[rgba(14,116,144,0.2)]',
      iconColor: 'text-[#38bdf8]',
    },
    ocean: {
      base: '#040e1a',
      bg: 'from-[rgba(29,78,216,0.2)] to-[rgba(30,64,175,0.1)]',
      border: 'border-[rgba(29,78,216,0.2)]',
      icon: 'from-[rgba(29,78,216,0.3)] to-[rgba(30,64,175,0.2)]',
      iconColor: 'text-[#93c5fd]',
    },
    fresh: {
      base: '#041810',
      bg: 'from-[rgba(13,148,136,0.2)] to-[rgba(15,118,110,0.1)]',
      border: 'border-[rgba(13,148,136,0.2)]',
      icon: 'from-[rgba(13,148,136,0.3)] to-[rgba(15,118,110,0.2)]',
      iconColor: 'text-[#5eead4]',
    },
    glass: {
      base: 'transparent',
      bg: 'from-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.03)]',
      border: 'border-[rgba(255,255,255,0.1)]',
      icon: 'from-[rgba(255,255,255,0.1)] to-[rgba(255,255,255,0.05)]',
      iconColor: 'text-[#7dd3fc]',
    },
    wave: {
      base: '#04121c',
      bg: 'from-[rgba(56,189,248,0.18)] to-[rgba(14,165,233,0.08)]',
      border: 'border-[rgba(56,189,248,0.2)]',
      icon: 'from-[rgba(56,189,248,0.28)] to-[rgba(14,165,233,0.15)]',
      iconColor: 'text-[#7dd3fc]',
    },
  };

  const style = variantStyles[variant] || variantStyles.primary;

  return (
    <button
      onClick={onClick}
      style={{ backgroundColor: style.base !== 'transparent' ? style.base : undefined }}
      className={`
        relative overflow-hidden
        w-full text-right
        p-4 rounded-2xl
        bg-gradient-to-br ${style.bg}
        border ${style.border}
        backdrop-blur-lg
        transition-all duration-300 ease-out
        hover:scale-[1.02] hover:shadow-lg
        active:scale-[0.98]
        group
        ${className}
      `}
    >
      {/* Shine */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none group-hover:opacity-30 transition-opacity"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 60%)'
        }}
      />

      <div className="relative z-10">
        {Icon && (
          <div className={`
            w-12 h-12 rounded-xl mb-3
            flex items-center justify-center
            bg-gradient-to-br ${style.icon}
            border border-[rgba(255,255,255,0.15)]
            transition-transform duration-300 group-hover:scale-110
            ${style.iconColor}
          `}>
            <Icon className="w-6 h-6" />
          </div>
        )}
        <p className="text-white font-bold text-base mb-1">{title}</p>
        <p className="text-white/50 text-sm">{subtitle}</p>
      </div>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Water Link Button — زر رابط مائي
// ═══════════════════════════════════════════════════════════════════════════════
export const WaterLink = ({ 
  children,
  href,
  onClick,
  external = false,
  className = '',
}) => {
  const handleClick = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={`
        inline-flex items-center gap-1
        text-[#0ABAB5] font-semibold text-sm
        hover:text-[#00D4AA]
        transition-colors duration-200
        group
        ${className}
      `}
    >
      {children}
      <svg 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-[-2px]"
      >
        <path strokeLinecap="round" d="M15 19l-7-7 7-7" />
      </svg>
    </a>
  );
};

export default {
  WaterButton,
  WaterIconButton,
  WaterFab,
  WaterButtonGroup,
  WaterSegmentedControl,
  WaterActionCard,
  WaterLink,
};
